//
// Copyright 2024 DXOS.org
//
import * as Schema from 'effect/Schema';
import { BaseError } from '@dxos/errors';
import { invariant } from '@dxos/invariant';
import { SpaceId } from '@dxos/keys';
/**
 * HTTP header sent on every Edge request to classify traffic for metering.
 */
export const EDGE_CLIENT_TAG_HEADER = 'X-DXOS-Client-Tag';
/**
 * HTTP header sent on every Edge request to provide a BYOK (Bring Your Own Key) for AI services.
 */
export const BYOK_HEADER = 'X-BYOK';
// TODO(burdon): Rename EdgerRouterEndpoint.
// If we would rename it, we need to be careful to not break composer production.
export var EdgeService;
(function (EdgeService) {
    EdgeService["AUTOMERGE_REPLICATOR"] = "automerge-replicator";
    EdgeService["SUBDUCTION_REPLICATOR"] = "subduction-replicator";
    /**
     * Control feed replicator (hypercore append only logs) for the space.
     */
    // TODO(mykola): Remove once we migrate to keyhive for access control.
    EdgeService["FEED_REPLICATOR"] = "feed-replicator";
    /**
     * Feed replicator (ordered data list) for the space.
     */
    // TODO(mykola): Rename to FEED_REPLICATOR when we migrate to keyhive for access control.
    EdgeService["QUEUE_REPLICATOR"] = "queue-replicator";
    EdgeService["SWARM"] = "swarm";
    EdgeService["SIGNAL"] = "signal";
    EdgeService["STATUS"] = "status";
})(EdgeService || (EdgeService = {}));
const _SerializedError = Schema.Struct({
    name: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
    context: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.Any })),
    stack: Schema.optional(Schema.String),
    cause: Schema.optional(Schema.suspend(() => SerializedError)),
});
export const SerializedError = _SerializedError;
/**
 * Use this to create a response from the Edge service.
 */
export const EdgeResponse = Object.freeze({
    success: (data, status = 200) => {
        invariant(status >= 200 && status < 300, 'Status code must be in the 2xx range');
        const headers = new Headers({ 'Content-Type': 'application/json' });
        return new Response(JSON.stringify({
            success: true,
            data,
        }), {
            status,
            headers,
        });
    },
    failure: ({ message, error, errorEncoded, data, shouldRetryAfter, status = 500, }) => {
        const headers = new Headers({ 'Content-Type': 'application/json' });
        if (shouldRetryAfter) {
            headers.set('Retry-After', String(shouldRetryAfter));
        }
        return new Response(JSON.stringify({
            success: false,
            message,
            data,
            error: error ? ErrorCodec.encode(error) : errorEncoded,
        }), {
            status,
            headers,
        });
    },
});
/**
 * Note: Do not change the values of these enums, this values are stored in the FunctionVersions database.
 */
export const FunctionRuntimeKind = Schema.Enums({
    // https://developers.cloudflare.com/cloudflare-for-platforms/workers-for-platforms/
    WORKERS_FOR_PLATFORMS: 'WORKERS_FOR_PLATFORMS',
    // https://developers.cloudflare.com/workers/runtime-apis/bindings/worker-loader/
    WORKER_LOADER: 'WORKER_LOADER',
});
export var EdgeAgentStatus;
(function (EdgeAgentStatus) {
    EdgeAgentStatus["ACTIVE"] = "active";
    EdgeAgentStatus["INACTIVE"] = "inactive";
    EdgeAgentStatus["NOT_FOUND"] = "not_found";
})(EdgeAgentStatus || (EdgeAgentStatus = {}));
export var OAuthProvider;
(function (OAuthProvider) {
    OAuthProvider["ATLASSIAN"] = "atlassian";
    OAuthProvider["ATPROTO"] = "atproto";
    /** @deprecated Use ATPROTO instead. */
    OAuthProvider["BLUESKY"] = "bluesky";
    OAuthProvider["DISCORD"] = "discord";
    OAuthProvider["GITHUB"] = "github";
    OAuthProvider["GOOGLE"] = "google";
    OAuthProvider["LINEAR"] = "linear";
    OAuthProvider["NOTION"] = "notion";
    OAuthProvider["SLACK"] = "slack";
    OAuthProvider["TRELLO"] = "trello";
})(OAuthProvider || (OAuthProvider = {}));
/** atproto OAuth scopes for the Atmosphere integration and account-recovery flows. */
export const ATPROTO_OAUTH_SCOPES = ['atproto', 'transition:generic', 'transition:email'];
export const InitiateOAuthFlowRequestSchema = Schema.Struct({
    provider: Schema.Enums(OAuthProvider),
    spaceId: Schema.String.pipe(Schema.filter(SpaceId.isValid)), // TODO(burdon): Use SpaceId.
    accessTokenId: Schema.String,
    scopes: Schema.mutable(Schema.Array(Schema.String)),
    // Set to true if we don't want periodic token refreshes in background, for cases like account connect
    noRefresh: Schema.optional(Schema.Boolean),
    // Provider-specific (user handle or did for atproto) hint for auth server resolution
    loginHint: Schema.optional(Schema.String),
    // Return a 302 redirect to composer://oauth/callback instead of HTML.
    // Required for ASWebAuthenticationSession (iOS) which blocks JavaScript redirects.
    nativeAppRedirect: Schema.optional(Schema.Boolean),
    // OAuth-based account recovery: when purpose === 'register', kms-service writes a
    // recovery binding for `identityKey` after the OAuth flow completes; when 'recovery',
    // kms-service mints a one-time `recoveryProof` the client forwards to db-service.
    registerRecovery: Schema.optional(Schema.Boolean),
    identityKey: Schema.optional(Schema.String),
    purpose: Schema.optional(Schema.Literal('register', 'recovery')),
});
export var EdgeWebsocketProtocol;
(function (EdgeWebsocketProtocol) {
    EdgeWebsocketProtocol["V0"] = "edge-ws-v0";
    /**
     * Enables message framing and muxing by service-id.
     */
    EdgeWebsocketProtocol["V1"] = "edge-ws-v1";
})(EdgeWebsocketProtocol || (EdgeWebsocketProtocol = {}));
export const DocumentCodec = Object.freeze({
    encode: (doc) => Buffer.from(doc).toString('base64'),
    decode: (doc) => new Uint8Array(Buffer.from(doc, 'base64')),
});
const MAX_ERROR_DEPTH = 3;
/**
 * Codec for serializing and deserializing Edge Errors.
 */
export const ErrorCodec = Object.freeze({
    encode: (err, depth = 0) => ({
        name: 'name' in err ? err.name : err.code || 'Error',
        message: err.message,
        stack: err.stack,
        cause: err.cause instanceof Error && depth < MAX_ERROR_DEPTH ? ErrorCodec.encode(err.cause, depth + 1) : undefined,
    }),
    decode: (serializedError, depth = 0) => {
        let err;
        if (typeof serializedError.name === 'string') {
            err = new BaseError(serializedError.name, {
                message: serializedError.message ?? 'Unknown error',
                cause: serializedError.cause && depth < MAX_ERROR_DEPTH
                    ? ErrorCodec.decode(serializedError.cause, depth + 1)
                    : undefined,
                context: serializedError.context,
            });
            if (serializedError.stack) {
                Object.defineProperty(err, 'stack', {
                    value: serializedError.stack,
                });
            }
        }
        else {
            err = new Error(serializedError.message ?? 'Unknown error', {
                cause: serializedError.cause && depth < MAX_ERROR_DEPTH
                    ? ErrorCodec.decode(serializedError.cause, depth + 1)
                    : undefined,
            });
            if (serializedError.stack) {
                Object.defineProperty(err, 'stack', {
                    value: serializedError.stack,
                });
            }
        }
        return err;
    },
});
/**
 * Codec for serializing and deserializing Edge unhandled HTTP errors.
 */
export const EdgeHttpErrorCodec = Object.freeze({
    encode: (err) => new Response(JSON.stringify({
        error: ErrorCodec.encode(err),
    }), { status: 500 }),
    decode: async (response) => {
        if (response.headers.get('Content-Type') !== 'application/json') {
            const body = await response.clone().text();
            return new Error(body.slice(0, 256));
        }
        const body = await response.clone().json();
        if (!('error' in body)) {
            return undefined;
        }
        return ErrorCodec.decode(body.error);
    },
});
//
// Account / Invitation
//
export const INVITATION_CODE_LENGTH = 8;
export const DEFAULT_INVITATIONS_PER_ACCOUNT = 5;
/** Crockford base32 alphabet (no I, L, O, U). Case-insensitive on the wire. */
export const INVITATION_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const InvitationCodeSchema = Schema.String.pipe(Schema.pattern(new RegExp(`^[${INVITATION_CODE_ALPHABET}]{${INVITATION_CODE_LENGTH}}$`)));
export const CheckEmailExistsRequestSchema = Schema.Struct({
    email: Schema.String,
});
export const ValidateInvitationCodeRequestSchema = Schema.Struct({
    code: InvitationCodeSchema,
});
/**
 * Body of `POST /account/login`. Existing-account email recovery only --
 * unlike `/account/signup`, this never creates new identities or waitlist rows.
 */
export const LoginRequestSchema = Schema.Struct({
    email: Schema.String,
    identityDid: Schema.optional(Schema.String),
    identityKey: Schema.optional(Schema.String),
});
/**
 * Response from `POST /account/login`. The shape is identical regardless of
 * whether the email is registered, so the endpoint is safe against enumeration.
 * Regular emails are delivered out-of-band and the response is `{}`.
 */
export const LoginResponseSchema = Schema.Struct({
    token: Schema.optional(Schema.String),
    needsIdentity: Schema.optional(Schema.Boolean),
    admitted: Schema.optional(Schema.Boolean),
});
// Two-step signup: invitation code + identity DID + email.
export const RedeemInvitationCodeRequestSchema = Schema.Struct({
    code: Schema.optional(InvitationCodeSchema),
    identityDid: Schema.optional(Schema.String),
    /** Raw hex public key, stored alongside the DID for the magic-link recovery flow. */
    identityKey: Schema.optional(Schema.String),
    email: Schema.String,
});
export const GetAccountResponseSchema = Schema.Struct({
    identityDid: Schema.String,
    email: Schema.String,
    emailVerified: Schema.Boolean,
    /** ISO timestamp. */
    createdAt: Schema.String,
    invitationsRemaining: Schema.Number,
});
export const AccountInvitationSchema = Schema.Struct({
    code: Schema.String,
    /** ISO timestamp. */
    createdAt: Schema.String,
    redeemedByIdentityDid: Schema.optional(Schema.String),
    /** ISO timestamp. */
    redeemedAt: Schema.optional(Schema.String),
});
export const ListAccountInvitationsResponseSchema = Schema.Struct({
    invitations: Schema.Array(AccountInvitationSchema),
});
/**
 * Submitted by users without an Account who want to request access. Captured
 * by Hub for admin follow-up (e.g. mailing list, Discord notification).
 */
export const RequestAccessRequestSchema = Schema.Struct({
    email: Schema.String,
    /** Optional: identity DID the user is currently signed in as. */
    identityDid: Schema.optional(Schema.String),
    /** Optional free-form message from the requester. */
    message: Schema.optional(Schema.String),
});
//
// Metering (VP auth)
//
/** Structured usage key aligned with {@link MeteringLimitSchema} (without cap/window fields). */
export const MeteringUsageKeySchema = Schema.Struct({
    /** Event type (e.g. `ai`). */
    eventType: Schema.String,
    /** Value key being metered (e.g. `outputTokens`). */
    valueKey: Schema.String,
    /**
     * Positional match against the event's subtype segments (e.g. model); `*` matches any
     * segment, and positions beyond the pattern are unconstrained.
     */
    subtypePattern: Schema.Array(Schema.String),
});
/** Rolling-window usage total for a structured key. */
export const MeteringUsageItemSchema = Schema.Struct({
    ...MeteringUsageKeySchema.fields,
    amount: Schema.Number,
});
/** A single raw usage bucket (1h resolution) for a structured key. */
export const MeteringUsageBucketSchema = Schema.Struct({
    ...MeteringUsageKeySchema.fields,
    /** Bucket start timestamp (epoch ms, floored to the hour). */
    bucketStart: Schema.Number,
    amount: Schema.Number,
});
export const MeteringLimitSchema = Schema.Struct({
    /** Event type the limit applies to (e.g. `ai`). */
    eventType: Schema.String,
    /** Value key being limited (e.g. `outputTokens`). */
    valueKey: Schema.String,
    /**
     * Positional match against the event's subtype segments (e.g. model); `*` matches any
     * segment, and positions beyond the pattern are unconstrained.
     */
    subtypePattern: Schema.Array(Schema.String),
    /** Rolling-window cap; `null` means unlimited. */
    limit: Schema.NullOr(Schema.Number),
    /** Window duration in seconds. */
    windowDuration: Schema.Number,
});
export const GetProfileUsageResponseSchema = Schema.Struct({
    profileId: Schema.String,
    usage: Schema.Array(MeteringUsageItemSchema),
    limits: Schema.Array(MeteringLimitSchema),
    buckets: Schema.Array(MeteringUsageBucketSchema),
});
export const AdminGrantInvitationsRequestSchema = Schema.Struct({
    identityDid: Schema.String,
    count: Schema.Number,
});
export const AdminCreateInvitationCodesRequestSchema = Schema.Struct({
    count: Schema.Number,
    note: Schema.optional(Schema.String),
});
export const AdminRevokeInvitationCodeRequestSchema = Schema.Struct({
    code: InvitationCodeSchema,
});
//# sourceMappingURL=edge.js.map